import React from 'react';
import PeopleActivityScreen, { ActivitySession } from '../PeopleActivityScreen';
import { usePulseCheck } from './hooks/usePulseCheck';
import PulseCheckContent from './PulseCheckContent';
import { discardActivity } from '../../peopleHubApi';

// Re-export the shared session type so App.tsx / PeopleHub/index.tsx
// can import it from this path without knowing the internal structure.
export type { ActivitySession };

// ─── Props ────────────────────────────────────────────────────────────────────
interface PulseCheckScreenProps {
  session: ActivitySession;
  onExit: () => void;
  onComplete: () => void;
}

// ─── Screen ───────────────────────────────────────────────────────────────────

/**
 * The entry point rendered by App.tsx for the "PeopleHub PulseCheck" screen.
 *
 * Wires together:
 *   usePulseCheck   — all Pulse Check state + handlers
 *   PulseCheckContent — the activity-specific section list (no layout)
 *   PeopleActivityScreen — shared shell: scrollable body + fixed footer
 */
const PulseCheckScreen: React.FC<PulseCheckScreenProps> = ({
  session,
  onExit,
  onComplete,
}) => {
  const {
    metricRows,
    periodMetricRows,
    feedback,
    openItems,
    historyItems,
    actionDraft,
    sigData,
    setFeedback,
    setActionDraft,
    setSigData,
    isSignatureUploading,
    setIsSignatureUploading,
    handleFollowUpResult,
    handleFollowUpFeedback,
    validationError,
    canComplete,
    isSubmitting,
    isSavingDraft,
    handleSaveDraft,
    handleComplete,
  } = usePulseCheck(session);

  // Discard the in-progress activity row when the user exits without completing it
  // (pulse check is single-stage, so Exit always discards directly — no Leave modal).
  // Only a brand-new, untouched activity is discarded; anything already saved/resumed stays.
  const handleDiscardExit = async () => {
    if (session.activityId != null && session.status === 'NEW') {
      try {
        await discardActivity(session.activityId);
      } catch (err) {
        console.warn('Failed to discard pulse check activity:', err);
      }
    }
    onExit();
  };

  return (
    <PeopleActivityScreen
      session={session}
      onExit={onExit}
      onDiscardExit={handleDiscardExit}
      onComplete={() => handleComplete(onComplete)}
      onSaveDraft={() => handleSaveDraft(onExit)}
      isSavingDraft={isSavingDraft}
      canComplete={canComplete}
      isSubmitting={isSubmitting}
      validationError={validationError}
    >
      <PulseCheckContent
        session={session}
        metricRows={metricRows}
        periodMetricRows={periodMetricRows}
        feedback={feedback}
        openItems={openItems}
        historyItems={historyItems}
        actionDraft={actionDraft}
        sigData={sigData}
        setFeedback={setFeedback}
        setActionDraft={setActionDraft}
        setSigData={setSigData}
        onSignatureUploadingChange={setIsSignatureUploading}
        onFollowUpResult={handleFollowUpResult}
        onFollowUpFeedback={handleFollowUpFeedback}
      />
    </PeopleActivityScreen>
  );
};

export default PulseCheckScreen;
